import React, { useCallback, useContext, useState } from "react";
import { useLocation } from "react-router-dom";
import { Box, Card, Typography, Button, Alert, TextField, CircularProgress, useTheme, useMediaQuery } from "@mui/material";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import { useRegistroTurno } from "../hooks";
import { validarNumeroPedido } from "../helpers/registro";
import { ModalPedidosCliente } from "../components";
import { StateContext } from "../../../components/context/StateProvider";
import Toasts from "../../../components/notificacion/Toasts";

const sxBotonGrande = { minHeight: 56, fontSize: 18, fontWeight: 600, textTransform: "none" };
const sxPrimario = {
    ...sxBotonGrande,
    backgroundColor: "#cc071e",
    "&:hover": { backgroundColor: "#a30618" },
    "&:disabled": { backgroundColor: "#6c757d", color: "#fff" },
};
const sxSecundario = { ...sxBotonGrande, color: "#495057", borderColor: "#6c757d" };
const sxTarjeta = { boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2, p: 3 };
const sxExito = { fontSize: { xs: 18, md: 24 }, fontWeight: 700, alignItems: "center" };

const textoExito = (entrada, sinPedido) =>
    `Turno registrado correctamente${sinPedido ? " (sin pedido)" : ""}: ${entrada?.numero ?? ""}`;

/**
 * Alta de turnos en una cola (?id=), pensada para la tablet táctil del mostrador.
 * DECISION: no sigue el patrón de página de listado porque no es un listado (dos
 * tarjetas centradas), pero conserva sus colores, sombras y tipografía. La lógica
 * de alta vive en useRegistroTurno y la validación en helpers/registro.js.
 */
const RegistroTurno = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
    const { state, updateState } = useContext(StateContext);
    const idCola = new URLSearchParams(useLocation().search).get("id");
    const { registrando, registrarConPedido, registrarSinPedido } = useRegistroTurno({ idCola, ejercicio: state.year });

    const [numero, setNumero] = useState("");
    const [errorNumero, setErrorNumero] = useState(null);
    const [exito, setExito] = useState(null);
    const [modalAbierto, setModalAbierto] = useState(false);
    const [accion, setAccion] = useState(null); // 'pedido' | 'sinPedido' | null
    const [eleccion, setEleccion] = useState(null);

    const mostrarToast = useCallback(
        (mensaje, tipo) => updateState({ toasts: [...state.toasts, Toasts.generateToast(mensaje, tipo)] }),
        [state.toasts, updateState]
    );

    const handleCambioNumero = useCallback((e) => {
        setNumero(e.target.value);
        setErrorNumero(null);
        setExito(null);
    }, []);

    const handleSubmit = useCallback(
        (e) => {
            e.preventDefault();
            const error = validarNumeroPedido(numero);
            setErrorNumero(error);
            if (!error) setModalAbierto(true);
        },
        [numero]
    );

    const ejecutar = useCallback(
        async (tipoAccion, alta, sinPedido) => {
            setExito(null);
            setAccion(tipoAccion);
            try {
                const entrada = await alta();
                if (!entrada) return false;
                setExito(textoExito(entrada, sinPedido));
                return true;
            } catch (err) {
                mostrarToast(err.message || "No se pudo registrar el turno.", "error");
                return false;
            } finally {
                setAccion(null);
            }
        },
        [mostrarToast]
    );

    const handleConfirmarPedido = useCallback(
        async (incluirTodos) => {
            setEleccion(incluirTodos);
            const registrado = await ejecutar("pedido", () => registrarConPedido(numero, incluirTodos), false);
            setModalAbierto(false);
            if (registrado) setNumero("");
        },
        [ejecutar, registrarConPedido, numero]
    );

    const handleSinPedido = useCallback(
        () => ejecutar("sinPedido", registrarSinPedido, true),
        [ejecutar, registrarSinPedido]
    );

    const handleCerrarModal = useCallback(() => setModalAbierto(false), []);

    if (!idCola) {
        return (
            <Box sx={{ maxWidth: 600, mx: "auto", mt: 4, px: 2 }}>
                <Alert severity="warning">Falta la cola: abre esta pantalla desde la lista de colas</Alert>
            </Box>
        );
    }

    const cargando = (tipo) => registrando && accion === tipo;

    return (
        <Box sx={{ maxWidth: 600, mx: "auto", mt: isMobile ? 2 : 5, px: 2, display: "flex", flexDirection: "column", gap: 3 }}>
            <Card component="form" onSubmit={handleSubmit} noValidate sx={sxTarjeta}>
                <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700, color: "#212529", mb: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
                    <ConfirmationNumberIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
                    Solicitar turno con pedido
                </Typography>
                <Typography variant="body2" sx={{ color: "#6c757d", mb: 2 }}>
                    Escribe el número de pedido con el formato serie-número.
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "#495057" }}>
                    Número de pedido *
                </Typography>
                <TextField
                    fullWidth
                    value={numero}
                    onChange={handleCambioNumero}
                    disabled={registrando}
                    placeholder="Ej: 01-123"
                    error={Boolean(errorNumero)}
                    helperText={errorNumero}
                    inputProps={{ autoComplete: "off", style: { fontSize: 20 } }}
                />
                <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={registrando}
                    startIcon={cargando("pedido") ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : null}
                    sx={{ ...sxPrimario, mt: 3 }}
                >
                    {cargando("pedido") ? "Registrando..." : "Solicitar turno"}
                </Button>
            </Card>

            <Card sx={sxTarjeta}>
                <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700, color: "#212529", mb: 2 }}>
                    Solicitar turno sin pedido
                </Typography>
                <Button
                    variant="outlined"
                    fullWidth
                    disabled={registrando}
                    onClick={handleSinPedido}
                    startIcon={cargando("sinPedido") ? <CircularProgress size={20} sx={{ color: "#cc071e" }} /> : null}
                    sx={sxSecundario}
                >
                    {cargando("sinPedido") ? "Registrando..." : "Solicitar turno sin pedido"}
                </Button>
            </Card>

            {exito && (
                <Alert severity="success" sx={sxExito}>
                    {exito}
                </Alert>
            )}

            <ModalPedidosCliente
                show={modalAbierto}
                onHide={handleCerrarModal}
                onConfirm={handleConfirmarPedido}
                numero={numero}
                loading={cargando("pedido")}
                eleccionEnCurso={eleccion}
            />
        </Box>
    );
};

export default RegistroTurno;
